import { captureException } from '@sentry/browser';
import { DefaultBodyType, MockedRequest, rest, RestHandler } from 'msw';
import ApiClient, { IApiClient } from './ApiClient';
import { ApiResponse } from './ApiResponse';

type MSW_NETWORKS = 'NORMAL' | 'SLOW' | 'ERROR';

type MSW_HANDLERS = Record<
  MSW_NETWORKS,
  RestHandler<MockedRequest<DefaultBodyType>>
>;

export interface APIRouteParams<Params, ResponseObject> {
  client: ApiClient;
  path: string;
  method: keyof IApiClient;
  mockData: (params: Params) => ResponseObject;
  beforeRequest?: (params?: Params) => Params | undefined | FormData;
  headers?: { [key: string]: string };
  errorMessages?: { [key: string]: string };
}

type Route<Params, T> = (params?: Params) => Promise<T>;

export interface APIRoute<Params, T> {
  APIRoute: Route<Params, T>;
  MSW: MSW_HANDLERS;
}

export default function CreateAPIRoute<
  Params extends { [key: string]: any },
  ResponseObject,
  ErrorMessages = {}
>(
  config: APIRouteParams<Params, ResponseObject>
): APIRoute<Params, ResponseObject> {
  const { client, path, method, mockData, errorMessages } = config;

  let APIRoute: Route<Params, ResponseObject> | undefined;

  if (method === 'get') {
    APIRoute = async (params?: Params) => {
      let url = new URL(path, process.env.NEXT_PUBLIC_API_URL);

      if (config.beforeRequest) {
        params = config.beforeRequest(params) as Params;
      }

      if (params) {
        Object.keys(params).forEach((key) => {
          if (params) {
            url.searchParams.set(key, params[key]);
          }
        });
      }

      const data = await client.get<ApiResponse<ResponseObject>>(
        url.href,
        config.headers
      );

      if (!data.data) {
        throw new Error('API Error');
      }

      return data.data;
    };
  } else if (method === 'post') {
    APIRoute = async (params?: Params) => {
      let requestParams: Params | undefined | FormData = params;
      if (config.beforeRequest) {
        requestParams = config.beforeRequest(params);
      }

      try {
        const data = await client.post<
          Params | undefined | FormData,
          ApiResponse<ResponseObject>
        >(
          path,
          requestParams,
          config.headers ? { headers: config.headers } : undefined
        );

        if (!data.data) {
          throw new Error('API Error');
        }

        return data.data;
      } catch (e: any) {
        if (e instanceof Error) {
          if (config.errorMessages) {
            console.log(e.cause);
            let errorMessage = config.errorMessages[e.cause as string];

            throw new Error(e.message, { cause: errorMessage });
          }
        }

        throw new Error(e);
      }
    };
  } else if (method === 'patch') {
    APIRoute = async (params?: Params) => {
      if (config.beforeRequest) {
        params = config.beforeRequest(params) as Params;
      }

      const data = await client.patch<
        Params | undefined,
        ApiResponse<ResponseObject>
      >(path, params, config.headers);

      if (!data.data) {
        throw new Error('API Error');
      }

      return data.data;
    };
  } else if (method === 'delete') {
    APIRoute = async (params?: Params) => {
      if (config.beforeRequest) {
        params = config.beforeRequest(params) as Params;
      }

      const data = await client.delete<ApiResponse<ResponseObject>>(path, {
        data: params,
      });

      if (!data.data) {
        throw new Error('API Error');
      }

      return data.data;
    };
  }

  if (APIRoute === undefined) {
    throw new Error('Invalid method');
  }

  const MSW: MSW_HANDLERS = {
    NORMAL: rest[method](
      `${process.env.API_URL}${path}`,
      async (req, res, ctx) => {
        let params: Params;

        if (method === 'get') {
          params = req.params as Params;
        } else {
          params = (await req.json()) as Params;
        }

        return res(ctx.json({ data: mockData(params) }));
      }
    ),
    SLOW: rest[method](
      `${process.env.API_URL}${path}`,
      async (req, res, ctx) => {
        let params: Params;

        if (method === 'get') {
          params = req.params as Params;
        } else {
          params = (await req.json()) as Params;
        }

        await new Promise((resolve) =>
          setInterval(
            () => resolve(res(ctx.json({ data: mockData(params) }))),
            5000
          )
        );
      }
    ),
    ERROR: rest[method](`${process.env.API_URL}${path}`, (req, res, ctx) => {
      return res(ctx.json({ error: 'Server Error' }));
    }),
  };

  return {
    APIRoute,
    MSW,
  };
}
